<template>
  <v-row class="cost-center-allocation-compact">
    <!-- Label column (like other form fields) -->
    <v-col class="label" sm="3">
      {{ label }}
    </v-col>

    <!-- Content column -->
    <v-col sm="9">
      <div class="d-flex align-center">
        <!-- Chips display -->
        <div class="flex-grow-1">
          <!-- Empty state -->
          <span v-if="!value || value.length === 0" class="grey--text empty-state-text">
            {{ $t('cost-center.no-allocations') }}
          </span>

          <!-- Allocations chips (outlined, no color) -->
          <v-chip
            v-else
            v-for="allocation in normalizedAllocations"
            :key="allocation.costCenterId"
            small
            outlined
            class="mr-2"
          >
            {{ allocation.costCenterName }} ({{ allocation.sharePercent.toFixed(1) }}%)
          </v-chip>
        </div>

        <!-- Edit button -->
        <v-btn
          small
          outlined
          color="primary"
          class="ml-3"
          @click="openModal"
        >
          <v-icon small left>mdi-pencil</v-icon>
          {{ $t('cost-center.edit') }}
        </v-btn>
      </div>
    </v-col>

    <!-- Modal -->
    <cost-center-allocation-modal
      v-model="modalOpen"
      :allocations="normalizedAllocations"
      :cost-centers="costCenters"
      :organization-id="organizationId"
      @save="onSave"
    />
  </v-row>
</template>

<script>
import CostCenterAllocationModal from './CostCenterAllocationModal.vue'
import CostCenterRepository from "@repository/CostCenterRepository";

export default {
  name: 'CostCenterAllocationCompact',

  components: {
    CostCenterAllocationModal
  },

  props: {
    // v-model: Array of {costCenterId, costCenterName, sharePercent}
    value: {
      type: Array,
      default: () => []
    },
    // Organization ID
    organizationId: {
      type: String,
      required: true
    },
    // Label
    label: {
      type: String,
      default() {
        return this.$tc('cost-center.allocations')
      }
    }
  },

  data() {
    return {
      modalOpen: false
    }
  },

  mounted() {
    this.loadCostCenters()
  },

  watch: {
    // The organization id can arrive after mount (parent loads it async) — (re)load once it's set,
    // and never fire the search with an undefined id (which would hit /organization/undefined/...).
    organizationId(id) {
      if (id) {
        this.loadCostCenters()
      }
    }
  },

  computed: {
    // Normalize allocations from backend format (id, name) to frontend format (costCenterId, costCenterName)
    normalizedAllocations() {
      if (!this.value || this.value.length === 0) return []

      return this.value.map(allocation => {
        // Backend sends: { id, name, sharePercent }
        // Frontend expects: { costCenterId, costCenterName, sharePercent }
        const costCenterId = allocation.costCenterId || allocation.id
        const costCenterName = allocation.costCenterName || allocation.name

        return {
          costCenterId,
          costCenterName,
          sharePercent: parseFloat(allocation.sharePercent || 0)
        }
      })
    }
  },

  methods: {
    openModal() {
      this.modalOpen = true
    },

    onSave(newAllocations) {
      this.$emit('input', newAllocations)
      this.modalOpen = false
    },

    async loadCostCenters() {
      if (!this.organizationId) {
        return // no organization yet → skip (avoids /organization/undefined/cost-center/search)
      }
      try {
        const response = await CostCenterRepository.search(this.organizationId, {
          offset: 0,
          limit: 1000,
          order: [{ field: 'name', direction: 'asc' }]
        }, 'charging-point-form')
        this.costCenters = response.items || []
      } catch (error) {
        this.notifyError(error)
      }
    },
  }
}
</script>

<style scoped>
.cost-center-allocation-compact {
  align-items: center;
}

.empty-state-text {
  font-size: 14px;
  font-weight: normal;
  text-transform: none;
}
</style>
